import { 
  DEFAULT_ACCOUNT_MAPPING, 
  XERO_ENDPOINTS,
  XERO_TAX_TYPES 
} from '../config/xero.config';
import XeroCredentialsService from './xero-credentials.service';
import { 
  XeroAuthState, 
  XeroContact, 
  XeroInvoice, 
  XeroSyncData, 
  XeroSyncResult,
  XeroLineItem,
  XeroWebhookEvent,
  XeroPayment,
  XeroPaymentResult,
  XeroJournalEntry
} from '../types/xero.types';
import { supabase } from '../lib/supabase';

export class XeroService {
  private static authState: XeroAuthState = {
    isConnected: false
  };

  /**
   * Initiate Xero OAuth authentication using stored credentials
   */
  static async initiateAuth(organizationId: string): Promise<string | null> {
    try {
      const authUrl = await XeroCredentialsService.generateAuthUrl(organizationId);
      return authUrl;
    } catch (error) {
      console.error('Failed to initiate Xero auth:', error);
      return null;
    }
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  static async handleCallback(code: string, state: string, organizationId: string): Promise<boolean> {
    console.log('handleCallback called with:', { code: code?.substring(0, 10) + '...', state, organizationId });
    
    try {
      // Exchange code for tokens using the credentials service
      const result = await XeroCredentialsService.exchangeCodeForTokens(organizationId, code, state);
      
      if (!result.success) {
        console.error('Token exchange failed:', result.error);
        throw new Error(result.error || 'Failed to exchange code for tokens');
      }

      const tokens = result.data;
      console.log('Tokens received:', { 
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        expiresIn: tokens.expires_in 
      });
      
      // Store auth state in memory for this session
      this.authState = {
        isConnected: true,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: new Date(Date.now() + tokens.expires_in * 1000),
        tenantId: tokens.tenant_id,
        organizationId: organizationId
      };

      // Store in localStorage for persistence
      localStorage.setItem('xero_auth', JSON.stringify(this.authState));
      console.log('Auth state saved to localStorage');
      
      return true;
    } catch (error) {
      console.error('Xero authentication failed:', error);
      return false;
    }
  }
  /**
   * Check if we're authenticated with Xero for a specific organization
   */
  static async isAuthenticated(organizationId?: string): Promise<boolean> {
    // Try to load from localStorage first
    const stored = localStorage.getItem('xero_auth');
    if (stored) {
      this.authState = JSON.parse(stored);
      
      // Verify organization match if provided
      if (organizationId && this.authState.organizationId !== organizationId) {
        console.log('Organization mismatch in stored auth state');
        this.authState.isConnected = false;
        localStorage.removeItem('xero_auth');
        return false;
      }
      
      // Check if token is expired or expires in the next 5 minutes
      const expiresAt = this.authState.expiresAt ? new Date(this.authState.expiresAt) : null;
      const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
      
      if (expiresAt && expiresAt <= fiveMinutesFromNow) {
        console.log('Access token expired or expiring soon, attempting refresh');
        const refreshed = await this.refreshAccessToken();
        if (!refreshed) {
          this.authState.isConnected = false;
          localStorage.removeItem('xero_auth');
          return false;
        }
      }
    } else if (organizationId) {
      // Check if we have valid tokens in the database
      const hasValidTokens = await XeroCredentialsService.hasValidTokens(organizationId);
      if (hasValidTokens) {
        // Load tokens from database into auth state
        const tokens = await XeroCredentialsService.getStoredTokens(organizationId);
        if (tokens.success && tokens.data) {
          this.authState = {
            isConnected: true,
            accessToken: tokens.data.access_token,
            refreshToken: tokens.data.refresh_token,
            expiresAt: new Date(tokens.data.expires_at),
            tenantId: tokens.data.tenant_id,
            organizationId: organizationId
          };
          localStorage.setItem('xero_auth', JSON.stringify(this.authState));
          return true;
        }
      }
    }
    
    return this.authState.isConnected;
  }

  /**
   * Refresh the access token using the refresh token
   */
  static async refreshAccessToken(): Promise<boolean> {
    if (!this.authState.refreshToken || !this.authState.organizationId) {
      console.error('No refresh token or organization ID available');
      return false;
    }

    try {
      console.log('Refreshing Xero access token');
      
      const result = await XeroCredentialsService.refreshTokens(
        this.authState.organizationId,
        this.authState.refreshToken
      );

      if (!result.success) {
        console.error('Token refresh failed:', result.error);
        return false;
      }

      const tokens = result.data;
      console.log('Token refresh successful');
      
      // Update auth state with new tokens
      this.authState = {
        ...this.authState,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token || this.authState.refreshToken,
        expiresAt: new Date(Date.now() + tokens.expires_in * 1000)
      };

      // Save updated auth state
      localStorage.setItem('xero_auth', JSON.stringify(this.authState));
      
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Sync vehicle sale to Xero
   */
  static async syncSaleToXero(syncData: XeroSyncData, organizationId: string): Promise<XeroSyncResult> {
    if (!(await this.isAuthenticated(organizationId))) {
      return { 
        success: false, 
        errors: ['Not authenticated with Xero. Please connect your account.'] 
      };
    }

    return this.executeWithRetry(async () => {
      // Step 1: Create or update customer contact
      const contactResult = await this.createOrUpdateContact(syncData.customer);
      if (!contactResult.success) {
        return contactResult;
      }

      // Step 2: Create invoice with tax breakdown
      const invoice = this.buildInvoiceFromSale(syncData, contactResult.contactId!);
      const invoiceResult = await this.createInvoice(invoice);
      
      if (!invoiceResult.success) {
        return invoiceResult;
      }

      // Step 3: Create journal entries for complex transactions
      if (syncData.tradeIn) {
        const journalResult = await this.createTradeInJournal(syncData);
        if (!journalResult.success) {
          return {
            success: true,
            invoiceId: invoiceResult.invoiceId,
            contactId: contactResult.contactId,
            warnings: [`Invoice created but trade-in journal failed: ${journalResult.errors?.join(', ')}`]
          };
        }
      }

      return {
        success: true,
        invoiceId: invoiceResult.invoiceId,
        contactId: contactResult.contactId
      };
    });
  }

  /**
   * Update existing invoice in Xero
   */
  static async updateInvoice(invoiceId: string, updateData: Partial<XeroInvoice>, organizationId: string): Promise<XeroSyncResult> {
    if (!(await this.isAuthenticated(organizationId))) {
      return { success: false, errors: ['Not authenticated with Xero'] };
    }

    return this.executeWithRetry(async () => {
      try {
        const response = await this.makeXeroApiCall(`/Invoices/${invoiceId}`, 'POST', {
          Invoices: [updateData]
        });

        if (response.Invoices && response.Invoices.length > 0) {
          return {
            success: true,
            invoiceId: response.Invoices[0].InvoiceID
          };
        }

        return {
          success: false,
          errors: ['Failed to update invoice in Xero']
        };
      } catch (error) {
        return {
          success: false,
          errors: [`Invoice update failed: ${error}`]
        };
      }
    });
  }

  /**
   * Void invoice in Xero
   */
  static async voidInvoice(invoiceId: string, organizationId: string, reason?: string): Promise<XeroSyncResult> {
    if (!(await this.isAuthenticated(organizationId))) {
      return { success: false, errors: ['Not authenticated with Xero'] };
    }

    return this.executeWithRetry(async () => {
      try {
        const response = await this.makeXeroApiCall(`/Invoices/${invoiceId}`, 'POST', {
          Invoices: [{
            Status: 'VOIDED',
            Reference: reason || 'Voided from dealership system'
          }]
        });

        if (response.Invoices && response.Invoices.length > 0) {
          return {
            success: true,
            invoiceId: response.Invoices[0].InvoiceID
          };
        }

        return {
          success: false,
          errors: ['Failed to void invoice in Xero']
        };
      } catch (error) {
        return {
          success: false,
          errors: [`Invoice void failed: ${error}`]
        };
      }
    });
  }

  /**
   * Get invoice from Xero
   */
  static async getInvoice(invoiceId: string, organizationId: string): Promise<XeroInvoice | null> {
    if (!(await this.isAuthenticated(organizationId))) {
      return null;
    }

    try {
      const response = await this.makeXeroApiCall(`/Invoices/${invoiceId}`);
      return response.Invoices && response.Invoices.length > 0 
        ? this.mapXeroInvoiceToLocal(response.Invoices[0]) 
        : null;
    } catch (error) {
      console.error('Failed to get invoice:', error);
      return null;
    }
  }

  /**
   * Get all invoices with pagination
   */
  static async getInvoices(organizationId: string, page = 1, pageSize = 100): Promise<XeroInvoice[]> {
    if (!(await this.isAuthenticated(organizationId))) {
      return [];
    }

    try {
      const response = await this.makeXeroApiCall(`/Invoices?page=${page}&pageSize=${pageSize}`);
      return (response.Invoices || []).map(this.mapXeroInvoiceToLocal);
    } catch (error) {
      console.error('Failed to get invoices:', error);
      return [];
    }
  }
  /**
   * Build Xero invoice from sale data with Australian tax compliance
   */
  private static buildInvoiceFromSale(syncData: XeroSyncData, contactId: string): XeroInvoice {
    const lineItems: XeroLineItem[] = [];
    const tax = syncData.taxCalculation;

    // Main vehicle sale line (includes stamp duty as it's a pass-through cost to customer)
    const vehicleTotalAmount = tax.subtotal + tax.stampDuty;
    const vehicleDescription = `${syncData.vehicle.year} ${syncData.vehicle.make} ${syncData.vehicle.model} (VIN: ${syncData.vehicle.vin})` +
      (tax.stampDuty > 0 ? ` - includes $${tax.stampDuty.toFixed(2)} stamp duty` : '');

    lineItems.push({
      description: vehicleDescription,
      quantity: 1,
      unitAmount: vehicleTotalAmount,
      lineAmount: vehicleTotalAmount,
      accountCode: DEFAULT_ACCOUNT_MAPPING.salesRevenue,
      taxType: XERO_TAX_TYPES.gstOnIncome
    });

    // LCT line item (if applicable) - typically included in vehicle price
    // Note: LCT is usually included in the purchase price and not shown separately on customer invoices

    // Stamp duty line item - included in main vehicle price
    // Note: We add stamp duty to the vehicle line item instead of separate line
    // to avoid tax type compatibility issues with different Xero configurations

    // Trade-in deduction (if applicable)
    if (syncData.tradeIn) {
      lineItems.push({
        description: `Trade-in: ${syncData.tradeIn.description}`,
        quantity: 1,
        unitAmount: -syncData.tradeIn.value,
        lineAmount: -syncData.tradeIn.value,
        accountCode: DEFAULT_ACCOUNT_MAPPING.tradeIns,
        taxType: XERO_TAX_TYPES.gstOnIncome
      });
    }

    return {
      type: 'ACCREC',
      contact: { contactId, name: syncData.customer.name },
      date: syncData.sale.date,
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days
      lineAmountTypes: 'Exclusive',
      lineItems,
      currencyCode: 'AUD',
      status: 'AUTHORISED',
      reference: syncData.sale.reference,
      subTotal: tax.subtotal,
      totalTax: tax.gst,
      total: tax.total,
      amountDue: tax.total
    };
  }
  /**
   * Create or update contact in Xero with two-way sync
   */
  private static async createOrUpdateContact(contact: XeroContact): Promise<XeroSyncResult> {
    try {
      // First, try to find existing contact by email or name
      let existingContact = null;
      if (contact.emailAddress) {
        try {
          const searchResponse = await this.makeXeroApiCall(
            `/Contacts?where=EmailAddress="${contact.emailAddress}"`
          );
          if (searchResponse.Contacts && searchResponse.Contacts.length > 0) {
            existingContact = searchResponse.Contacts[0];
          }
        } catch (searchError) {
          console.warn('Contact search by email failed:', searchError);
        }
      }

      // If not found by email, try by name
      if (!existingContact) {
        try {
          const nameSearchResponse = await this.makeXeroApiCall(
            `/Contacts?where=Name="${contact.name}"`
          );
          if (nameSearchResponse.Contacts && nameSearchResponse.Contacts.length > 0) {
            existingContact = nameSearchResponse.Contacts[0];
          }
        } catch (searchError) {
          console.warn('Contact search by name failed:', searchError);
        }
      }

      // Prepare contact data with conflict resolution
      const contactData = this.mergeContactData(existingContact, contact);
      
      let response;
      if (existingContact) {
        // Update existing contact
        response = await this.makeXeroApiCall(`/Contacts/${existingContact.ContactID}`, 'POST', {
          Contacts: [{ ...contactData, ContactID: existingContact.ContactID }]
        });
      } else {
        // Create new contact
        response = await this.makeXeroApiCall('/Contacts', 'POST', {
          Contacts: [contactData]
        });
      }

      if (response.Contacts && response.Contacts.length > 0) {
        return {
          success: true,
          contactId: response.Contacts[0].ContactID
        };
      }

      return {
        success: false,
        errors: ['Failed to create/update contact in Xero']
      };
    } catch (error) {
      return {
        success: false,
        errors: [`Contact operation failed: ${error}`]
      };
    }
  }

  /**
   * Merge contact data with conflict resolution
   */
  private static mergeContactData(existingContact: any, newContact: XeroContact): XeroContact {
    if (!existingContact) return newContact;

    // Conflict resolution strategy: prefer non-empty new data, keep existing for empty fields
    return {
      name: newContact.name || existingContact.Name,
      firstName: newContact.firstName || existingContact.FirstName,
      lastName: newContact.lastName || existingContact.LastName,
      emailAddress: newContact.emailAddress || existingContact.EmailAddress,
      phoneNumbers: newContact.phoneNumbers?.length 
        ? newContact.phoneNumbers 
        : existingContact.Phones?.map((p: any) => ({
            phoneType: p.PhoneType,
            phoneNumber: p.PhoneNumber
          })),
      addresses: newContact.addresses?.length 
        ? newContact.addresses 
        : existingContact.Addresses?.map((a: any) => ({
            addressType: a.AddressType,
            addressLine1: a.AddressLine1,
            addressLine2: a.AddressLine2,
            city: a.City,
            region: a.Region,
            postalCode: a.PostalCode,
            country: a.Country
          })),
      isCustomer: newContact.isCustomer !== undefined ? newContact.isCustomer : existingContact.IsCustomer,
      isSupplier: newContact.isSupplier !== undefined ? newContact.isSupplier : existingContact.IsSupplier
    };
  }

  /**
   * Sync contacts from Xero to local system
   */
  static async syncContactsFromXero(organizationId: string): Promise<XeroContact[]> {
    if (!(await this.isAuthenticated(organizationId))) {
      return [];
    }

    try {
      const contacts: XeroContact[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await this.makeXeroApiCall(`/Contacts?page=${page}&pageSize=100`);
        if (response.Contacts && response.Contacts.length > 0) {
          contacts.push(...response.Contacts.map(this.mapXeroContactToLocal));
          page++;
          hasMore = response.Contacts.length === 100;
        } else {
          hasMore = false;
        }
      }

      return contacts;
    } catch (error) {
      console.error('Failed to sync contacts from Xero:', error);
      return [];
    }
  }

  /**
   * Map Xero contact to local format
   */
  private static mapXeroContactToLocal(xeroContact: any): XeroContact {
    return {
      contactId: xeroContact.ContactID,
      name: xeroContact.Name,
      firstName: xeroContact.FirstName,
      lastName: xeroContact.LastName,
      emailAddress: xeroContact.EmailAddress,
      phoneNumbers: xeroContact.Phones?.map((p: any) => ({
        phoneType: p.PhoneType,
        phoneNumber: p.PhoneNumber
      })),
      addresses: xeroContact.Addresses?.map((a: any) => ({
        addressType: a.AddressType,
        addressLine1: a.AddressLine1,
        addressLine2: a.AddressLine2,
        city: a.City,
        region: a.Region,
        postalCode: a.PostalCode,
        country: a.Country
      })),
      isCustomer: xeroContact.IsCustomer,
      isSupplier: xeroContact.IsSupplier
    };
  }

  /**
   * Map Xero invoice to local format
   */
  private static mapXeroInvoiceToLocal(xeroInvoice: any): XeroInvoice {
    return {
      invoiceId: xeroInvoice.InvoiceID,
      invoiceNumber: xeroInvoice.InvoiceNumber,
      reference: xeroInvoice.Reference,
      type: xeroInvoice.Type as 'ACCREC' | 'ACCPAY',
      contact: xeroInvoice.Contact ? {
        contactId: xeroInvoice.Contact.ContactID,
        name: xeroInvoice.Contact.Name,
        firstName: xeroInvoice.Contact.FirstName,
        lastName: xeroInvoice.Contact.LastName,
        emailAddress: xeroInvoice.Contact.EmailAddress,
        isCustomer: xeroInvoice.Contact.IsCustomer,
        isSupplier: xeroInvoice.Contact.IsSupplier
      } : {
        name: 'Unknown Contact'
      },
      date: xeroInvoice.Date,
      dueDate: xeroInvoice.DueDate,
      lineAmountTypes: xeroInvoice.LineAmountTypes as 'Exclusive' | 'Inclusive' | 'NoTax',
      lineItems: xeroInvoice.LineItems?.map((item: any) => ({
        description: item.Description,
        quantity: item.Quantity,
        unitAmount: item.UnitAmount,
        lineAmount: item.LineAmount,
        accountCode: item.AccountCode,
        taxType: item.TaxType,
        tracking: item.Tracking
      })) || [],
      currencyCode: 'AUD' as const,
      status: xeroInvoice.Status as 'DRAFT' | 'SUBMITTED' | 'AUTHORISED' | 'PAID',
      subTotal: xeroInvoice.SubTotal,
      totalTax: xeroInvoice.TotalTax,
      total: xeroInvoice.Total,
      amountDue: xeroInvoice.AmountDue
    };
  }

  /**
   * Create invoice in Xero (checks for duplicates first)
   */
  private static async createInvoice(invoice: XeroInvoice): Promise<XeroSyncResult> {
    try {
      // Check if invoice with this reference already exists
      console.log(`Checking for existing invoice with reference: ${invoice.reference}`);

      const existingInvoicesResponse = await this.makeXeroApiCall(
        `/Invoices?where=Reference="${invoice.reference}"`,
        'GET'
      );

      // If invoice already exists, return the existing invoice ID
      if (existingInvoicesResponse.Invoices && existingInvoicesResponse.Invoices.length > 0) {
        const existingInvoice = existingInvoicesResponse.Invoices[0];
        console.log(`Invoice already exists: ${existingInvoice.InvoiceNumber} (${existingInvoice.InvoiceID})`);

        return {
          success: true,
          invoiceId: existingInvoice.InvoiceID,
          message: `Invoice ${existingInvoice.InvoiceNumber} already exists for this deal`
        };
      }

      // No duplicate found, create new invoice
      console.log('No existing invoice found, creating new one...');
      const response = await this.makeXeroApiCall('/Invoices', 'POST', {
        Invoices: [invoice]
      });

      if (response.Invoices && response.Invoices.length > 0) {
        const newInvoice = response.Invoices[0];
        console.log(`Invoice created: ${newInvoice.InvoiceNumber} (${newInvoice.InvoiceID})`);

        return {
          success: true,
          invoiceId: newInvoice.InvoiceID,
          message: `Invoice ${newInvoice.InvoiceNumber} created successfully`
        };
      }

      return {
        success: false,
        errors: ['Failed to create invoice in Xero']
      };
    } catch (error) {
      return {
        success: false,
        errors: [`Invoice creation failed: ${error}`]
      };
    }
  }

  /**
   * Make authenticated API call to Xero with enhanced error handling
   */
  private static async makeXeroApiCall(endpoint: string, method = 'GET', data?: any): Promise<any> {
    if (!this.authState.accessToken) {
      throw new Error('No access token available');
    }

    // Route ALL operations through backend to avoid CORS issues
    // CORS policy blocks direct browser requests to Xero API
    const shouldUseBackend = (
      endpoint.startsWith('/Invoices') ||
      endpoint.startsWith('/Contacts') ||
      endpoint.startsWith('/Accounts')
    );

    if (shouldUseBackend) {
      console.log(`Routing ${method} ${endpoint} through backend to avoid CORS`);
      return this.makeBackendApiCall(endpoint, method, data);
    }

    // Use direct API ONLY for endpoints not yet supported by backend
    const url = `${XERO_ENDPOINTS.api}${endpoint}`;
    console.log(`Making direct Xero API call: ${method} ${url}`);

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${this.authState.accessToken}`,
        'Content-Type': 'application/json',
        'Xero-tenant-id': this.authState.tenantId || '',
        'Accept': 'application/json'
      },
      body: data ? JSON.stringify(data) : undefined
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Xero API error: ${response.status} ${errorText}`);
      
      // Handle specific error cases
      if (response.status === 401) {
        // Token expired, try to refresh
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          // Retry the request with new token
          return this.makeXeroApiCall(endpoint, method, data);
        }
        throw new Error('Authentication failed');
      } else if (response.status === 429) {
        // Rate limited
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        // Server error
        throw new Error('Xero service temporarily unavailable');
      }
      
      throw new Error(`Xero API error: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    console.log('Xero API call successful');
    return result;
  }

  /**
   * Make API call through our backend proxy (Railway) to avoid CORS issues
   */
  private static async makeBackendApiCall(endpoint: string, method = 'GET', data?: any): Promise<any> {
    if (!this.authState.accessToken) {
      throw new Error('No access token available');
    }

    // Build Railway proxy URL - simply append the Xero endpoint path
    const backendEndpoint = `${XERO_ENDPOINTS.backend}${endpoint}`;

    console.log(`Making Backend API call: ${method} ${backendEndpoint}`);

    const response = await fetch(backendEndpoint, {
      method,
      headers: {
        'Authorization': `Bearer ${this.authState.accessToken}`,
        'Content-Type': 'application/json',
        'Xero-Tenant-Id': this.authState.tenantId || '',
        'Accept': 'application/json'
      },
      body: data ? JSON.stringify(data) : undefined
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Backend API error: ${response.status} ${errorText}`);
      
      // Handle specific error cases
      if (response.status === 401) {
        // Token expired, try to refresh
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          // Retry the request with new token
          return this.makeBackendApiCall(endpoint, method, data);
        }
        throw new Error('Authentication failed');
      }
      
      throw new Error(`Backend API error: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    console.log('Backend API call successful');
    return result;
  }

  /**
   * Create trade-in journal entry
   */
  private static async createTradeInJournal(syncData: XeroSyncData): Promise<XeroSyncResult> {
    if (!syncData.tradeIn) {
      return { success: true };
    }

    try {
      const journalEntry: XeroJournalEntry = {
        reference: `Trade-in: ${syncData.sale.reference}`,
        date: syncData.sale.date,
        journalLines: [
          {
            accountCode: DEFAULT_ACCOUNT_MAPPING.tradeIns,
            description: syncData.tradeIn.description,
            debitAmount: syncData.tradeIn.value,
            taxType: XERO_TAX_TYPES.gstOnIncome
          },
          {
            accountCode: DEFAULT_ACCOUNT_MAPPING.accountsReceivable,
            description: `Trade-in credit: ${syncData.customer.name}`,
            creditAmount: syncData.tradeIn.value
          }
        ]
      };

      const response = await this.makeXeroApiCall('/Journals', 'POST', {
        Journals: [journalEntry]
      });

      if (response.Journals && response.Journals.length > 0) {
        return {
          success: true,
          journalId: response.Journals[0].JournalID
        };
      }

      return {
        success: false,
        errors: ['Failed to create trade-in journal entry']
      };
    } catch (error) {
      return {
        success: false,
        errors: [`Trade-in journal creation failed: ${error}`]
      };
    }
  }

  /**
   * Disconnect from Xero
   */
  static disconnect(): void {
    this.authState = { isConnected: false };
    localStorage.removeItem('xero_auth');
  }

  /**
   * Get current auth state
   */
  static getAuthState(): XeroAuthState {
    return { ...this.authState };
  }

  /**
   * Execute function with retry logic
   */
  private static async executeWithRetry<T>(
    operation: () => Promise<T>,
    maxRetries = 3,
    baseDelay = 1000
  ): Promise<T> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        console.warn(`Attempt ${attempt} failed:`, error);
        
        // Don't retry on authentication errors or client errors (4xx)
        if (error instanceof Error && (
          error.message.includes('Authentication failed') ||
          error.message.includes('400') ||
          error.message.includes('403') ||
          error.message.includes('404')
        )) {
          throw error;
        }
        
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  }

  // ===== PAYMENT PROCESSING =====
  
  /**
   * Record payment against invoice
   */
  static async recordPayment(
    invoiceId: string,
    payment: XeroPayment,
    organizationId: string
  ): Promise<XeroPaymentResult> {
    if (!(await this.isAuthenticated(organizationId))) {
      return { success: false, errors: ['Not authenticated with Xero'] };
    }

    return this.executeWithRetry(async () => {
      try {
        const response = await this.makeXeroApiCall('/Payments', 'POST', {
          Payments: [{
            Invoice: { InvoiceID: invoiceId },
            Account: { Code: payment.account.code },
            Date: payment.date,
            Amount: payment.amount,
            Reference: payment.reference || 'Payment from dealership system'
          }]
        });

        if (response.Payments && response.Payments.length > 0) {
          return {
            success: true,
            paymentId: response.Payments[0].PaymentID
          };
        }

        return {
          success: false,
          errors: ['Failed to record payment in Xero']
        };
      } catch (error) {
        return {
          success: false,
          errors: [`Payment recording failed: ${error}`]
        };
      }
    });
  }

  /**
   * Record partial payment
   */
  static async recordPartialPayment(
    invoiceId: string,
    amount: number,
    payment: XeroPayment,
    organizationId: string
  ): Promise<XeroPaymentResult> {
    const partialPayment = { ...payment, amount };
    return this.recordPayment(invoiceId, partialPayment, organizationId);
  }

  /**
   * Get payments for an invoice
   */
  static async getInvoicePayments(invoiceId: string, organizationId: string): Promise<any[]> {
    if (!(await this.isAuthenticated(organizationId))) {
      return [];
    }

    try {
      const response = await this.makeXeroApiCall(`/Invoices/${invoiceId}/Payments`);
      return response.Payments || [];
    } catch (error) {
      console.error('Failed to get invoice payments:', error);
      return [];
    }
  }

  /**
   * Get bank accounts for payment processing
   */
  static async getBankAccounts(organizationId: string): Promise<any[]> {
    if (!(await this.isAuthenticated(organizationId))) {
      return [];
    }

    try {
      const response = await this.makeXeroApiCall('/Accounts?where=Type="BANK"');
      return response.Accounts || [];
    } catch (error) {
      console.error('Failed to get bank accounts:', error);
      return [];
    }
  }

  // ===== AUSTRALIAN TAX HANDLING =====
  
  /**
   * Calculate GST for Australian transactions
   */
  static calculateGST(amount: number, isGSTInclusive = false): { gstAmount: number; netAmount: number; totalAmount: number } {
    const GST_RATE = 0.10; // 10% GST in Australia
    
    if (isGSTInclusive) {
      const netAmount = amount / (1 + GST_RATE);
      const gstAmount = amount - netAmount;
      return {
        gstAmount: Math.round(gstAmount * 100) / 100,
        netAmount: Math.round(netAmount * 100) / 100,
        totalAmount: amount
      };
    } else {
      const gstAmount = amount * GST_RATE;
      const totalAmount = amount + gstAmount;
      return {
        gstAmount: Math.round(gstAmount * 100) / 100,
        netAmount: amount,
        totalAmount: Math.round(totalAmount * 100) / 100
      };
    }
  }

  /**
   * Calculate Luxury Car Tax
   */
  static calculateLCT(vehiclePrice: number, isForBusiness = false): number {
    const LCT_THRESHOLD_2024 = 71849; // Current threshold
    const LCT_BUSINESS_THRESHOLD_2024 = 84916; // Higher threshold for business vehicles
    const LCT_RATE = 0.33; // 33% LCT rate
    
    const threshold = isForBusiness ? LCT_BUSINESS_THRESHOLD_2024 : LCT_THRESHOLD_2024;
    
    if (vehiclePrice <= threshold) {
      return 0;
    }
    
    const lctableAmount = vehiclePrice - threshold;
    return Math.round(lctableAmount * LCT_RATE * 100) / 100;
  }

  /**
   * Apply correct tax codes for Australian transactions
   */
  static getAustralianTaxCode(transactionType: 'sale' | 'purchase' | 'exempt' | 'luxury'): string {
    switch (transactionType) {
      case 'sale':
        return XERO_TAX_TYPES.gstOnIncome;
      case 'purchase':
        return XERO_TAX_TYPES.inputTaxed;
      case 'exempt':
        return XERO_TAX_TYPES.gstFreeIncome;
      case 'luxury':
        return XERO_TAX_TYPES.luxury;
      default:
        return XERO_TAX_TYPES.gstOnIncome;
    }
  }
}
